import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CheckCircle2, MessageSquareQuote, Star } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "../lib/api";
import { useLanguage, dfLocale } from "../i18n";
import { Reveal } from "./Reveal";

const EMPTY_FORM = { name: "", rating: 0, text: "", privacy: false, website: "" };

const Stars = ({ value, size = "h-4 w-4", empty = "text-avorio/30" }) => (
  <div className="flex gap-0.5" role="img" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : empty}`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const { t, lang } = useLanguage();
  const [data, setData] = useState({ reviews: [], count: 0, average: null });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("scrivi-recensione")) setOpen(true);
  }, [searchParams]);

  const rif = searchParams.get("rif") || "";

  useEffect(() => {
    if (rif) {
      api.get(`/bookings/${rif}`).then((r) => set("name", r.data.first_name || "")).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rif]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggle = (o) => {
    setOpen(o);
    if (!o) {
      setDone(false);
      setForm(EMPTY_FORM);
      setHover(0);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error(t("Seleziona una valutazione in stelle."));
      return;
    }
    if (!form.privacy) {
      toast.error(t("Devi accettare l'informativa privacy per inviare la recensione."));
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", { ...form, lang, booking_id: rif });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("Errore durante l'invio. Riprova."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="recensioni" className="noise-overlay relative overflow-hidden bg-petrolio py-24 text-avorio sm:py-28" data-testid="reviews-section">
      <svg
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 text-salvia/10"
        viewBox="0 0 200 200"
        aria-hidden="true"
      >
        <circle cx="100" cy="100" r="98" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="100" cy="100" r="70" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="100" cy="100" r="42" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow !text-salvia">{t("Recensioni")}</p>
            <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              {t("Cosa dicono i nostri clienti.")}
            </h2>
            <p className="mt-4 max-w-xl text-base text-avorio/70">
              {t("Le esperienze di chi ha scelto Alsera Studio per il proprio progetto digitale.")}
            </p>
            {data.count > 0 && (
              <div className="mt-5 flex items-center gap-3" data-testid="reviews-summary">
                <Stars value={Math.round(data.average)} size="h-5 w-5" />
                <p className="text-sm font-semibold text-avorio/80">
                  {data.average.toLocaleString(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/5
                  · {data.count} {data.count === 1 ? t("recensione") : t("recensioni")}
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-avorio px-7 py-3.5 text-sm font-semibold text-petrolio shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]"
            data-testid="review-open-form"
          >
            <MessageSquareQuote className="h-4 w-4" />
            {t("Lascia la tua recensione")}
          </button>
        </Reveal>

        {data.count === 0 ? (
          <Reveal delay={0.1}>
            <div className="mt-14 rounded-3xl border border-dashed border-salvia/40 p-10 text-center" data-testid="reviews-empty">
              <p className="mx-auto max-w-md text-sm leading-relaxed text-avorio/70 sm:text-base">
                {t("Ancora nessuna recensione pubblicata. Se hai lavorato con noi, racconta la tua esperienza.")}
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {data.reviews.slice(0, 6).map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <figure
                  className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.06] p-7 backdrop-blur-sm transition-[transform,background-color] duration-500 hover:-translate-y-1.5 hover:bg-white/[0.09]"
                  data-testid={`review-card-${i}`}
                >
                  <Stars value={r.rating} />
                  <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-avorio/85 sm:text-base">
                    &ldquo;{r.text}&rdquo;
                  </blockquote>
                  {r.reply && (
                    <div className="mt-4 rounded-2xl border-l-2 border-salvia/50 bg-white/[0.04] p-4" data-testid={`review-reply-${i}`}>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-salvia">{t("Risposta di Alsera Studio")}</p>
                      <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-avorio/75">{r.reply}</p>
                    </div>
                  )}
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-white/10 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-salvia/25 text-sm font-bold text-salvia">
                      {r.name.trim().charAt(0).toUpperCase()}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-avorio">{r.name}</p>
                      <p className="text-xs text-avorio/50">
                        {format(parseISO(r.created_at), "d MMMM yyyy", { locale: dfLocale[lang] })}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={toggle}>
        <DialogContent className="sm:max-w-lg" data-testid="review-dialog">
          {done ? (
            <div className="py-4 text-center" data-testid="review-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-petrolio" />
              <DialogHeader className="mt-4">
                <DialogTitle className="text-center text-xl font-bold text-antracite">
                  {t("Grazie per la tua recensione!")}
                </DialogTitle>
                <DialogDescription className="pt-2 text-center text-antracite/70">
                  {t("È stata inviata correttamente e sarà visibile sul sito dopo l'approvazione.")}
                </DialogDescription>
              </DialogHeader>
              <button type="button" onClick={() => toggle(false)} className="btn-primary mt-6" data-testid="review-success-close">
                {t("Chiudi")}
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-antracite">{t("Scrivi una recensione")}</DialogTitle>
                <DialogDescription className="text-antracite/70">
                  {t("Condividi la tua esperienza con Alsera Studio: sarà pubblicata sul sito dopo una breve verifica.")}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-2 space-y-5" data-testid="review-form">
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                  <label htmlFor="review-website">Sito web</label>
                  <input id="review-website" type="text" data-testid="review-website-honeypot" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" />
                </div>
                <div>
                  <label htmlFor="review-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">
                    {t("Il tuo nome")} *
                  </label>
                  <input
                    id="review-name"
                    data-testid="review-name-input"
                    required
                    minLength={2}
                    maxLength={60}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">
                    {t("La tua valutazione")} *
                  </span>
                  <div className="flex gap-1.5" onMouseLeave={() => setHover(0)}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => set("rating", n)}
                        onMouseEnter={() => setHover(n)}
                        className="rounded-full p-1 transition-transform duration-200 hover:scale-110"
                        aria-label={`${n}/5`}
                        data-testid={`review-star-${n}`}
                      >
                        <Star className={`h-7 w-7 ${n <= (hover || form.rating) ? "fill-amber-400 text-amber-400" : "text-salvia/60"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="review-text" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">
                    {t("La tua esperienza")} *
                  </label>
                  <textarea
                    id="review-text"
                    data-testid="review-text-input"
                    required
                    minLength={10}
                    maxLength={600}
                    rows={4}
                    value={form.text}
                    onChange={(e) => set("text", e.target.value)}
                    className="input-field resize-none"
                    placeholder={t("Racconta come ti sei trovato…")}
                  />
                </div>
                <label className="flex items-start gap-3 text-sm text-antracite/70" data-testid="review-privacy-label">
                  <input
                    type="checkbox"
                    data-testid="review-privacy-checkbox"
                    checked={form.privacy}
                    onChange={(e) => set("privacy", e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-salvia/60 accent-petrolio"
                  />
                  <span>{t("Ho letto l'informativa privacy e acconsento alla pubblicazione del mio nome e della mia recensione su questo sito. *")}</span>
                </label>
                <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="review-submit-button">
                  {busy ? t("Invio in corso…") : t("Invia recensione")}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
